import React, { createContext, useState, useContext, useMemo, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { getFormFieldThemeOverrides, getDarkTextPalette, getLightTextPalette } from '../components/Common/formStyles';
import { BRAND, LIGHT, DARK } from '../theme/tokens';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage or default to 'light'
  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem('theme');
    return savedMode === 'dark' ? 'dark' : 'light';
  });

  // Persist theme to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('theme', mode);
  }, [mode]);

  // Update body background color based on theme
  useEffect(() => {
    const backgroundColor = mode === 'dark' ? DARK.background : LIGHT.background;
    document.body.style.backgroundColor = backgroundColor;
    document.documentElement.style.backgroundColor = backgroundColor;
  }, [mode]);

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const theme = useMemo(() => {
    const isDark = mode === 'dark';

    return createTheme({
      palette: {
        mode,
        primary: {
          main: isDark ? BRAND.primaryLight : BRAND.primary,
          dark: isDark ? '#6D75E8' : BRAND.primaryDark,
          light: isDark ? '#A5B4FC' : '#6366F1',
          contrastText: isDark ? '#0B1120' : '#FFFFFF',
        },
        secondary: {
          main: isDark ? BRAND.violetLight : BRAND.violet,
          contrastText: isDark ? '#0B1120' : '#FFFFFF',
        },
        success: { main: BRAND.success },
        warning: { main: BRAND.warning },
        error: { main: BRAND.error },
        text: isDark ? getDarkTextPalette() : getLightTextPalette(),
        divider: isDark ? DARK.divider : LIGHT.divider,
        background: {
          default: isDark ? DARK.background : LIGHT.background,
          paper: isDark ? DARK.paper : LIGHT.paper,
        },
        action: {
          hover: isDark ? 'rgba(148, 163, 184, 0.08)' : 'rgba(17, 24, 39, 0.04)',
          selected: isDark ? 'rgba(129, 140, 248, 0.16)' : 'rgba(79, 70, 229, 0.08)',
        },
      },
      shape: {
        borderRadius: 12,
      },
      shadows: [
        'none',
        '0px 1px 3px rgba(15, 23, 42, 0.06), 0px 1px 2px rgba(15, 23, 42, 0.04)',
        '0px 2px 6px rgba(15, 23, 42, 0.07)',
        '0px 4px 12px rgba(15, 23, 42, 0.08)',
        '0px 6px 16px rgba(15, 23, 42, 0.09)',
        '0px 8px 20px rgba(15, 23, 42, 0.10)',
        '0px 10px 24px rgba(15, 23, 42, 0.11)',
        ...Array(18).fill('0px 16px 40px rgba(15, 23, 42, 0.14)'),
      ],
      typography: {
        fontFamily: 'Inter, Roboto, sans-serif',
        h1: { fontWeight: 800, letterSpacing: '-0.03em' },
        h2: { fontWeight: 800, letterSpacing: '-0.025em' },
        h3: { fontWeight: 700, letterSpacing: '-0.02em' },
        h4: { fontWeight: 700, letterSpacing: '-0.02em' },
        h5: { fontWeight: 700, letterSpacing: '-0.015em' },
        h6: { fontWeight: 700, letterSpacing: '-0.01em' },
        subtitle1: { fontWeight: 600 },
        subtitle2: { fontWeight: 600 },
        button: { fontWeight: 600, letterSpacing: 0 },
      },
      components: {
        ...getFormFieldThemeOverrides(mode),
        MuiButton: {
          defaultProps: {
            disableElevation: true,
          },
          styleOverrides: {
            root: {
              textTransform: 'none',
              borderRadius: 10,
              padding: '8px 18px',
              fontWeight: 600,
            },
            containedPrimary: {
              '&:hover': {
                boxShadow: '0 6px 16px rgba(99, 102, 241, 0.35)',
              },
            },
            ...(isDark
              ? {
                  outlined: {
                    borderColor: 'rgba(129, 140, 248, 0.45)',
                    color: BRAND.primaryLight,
                    '&:hover': {
                      borderColor: 'rgba(129, 140, 248, 0.7)',
                      backgroundColor: 'rgba(129, 140, 248, 0.08)',
                    },
                  },
                }
              : {}),
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              borderRadius: 12,
              backgroundImage: 'none',
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 14,
              border: '1px solid',
              borderColor: isDark ? DARK.divider : LIGHT.divider,
              backgroundImage: 'none',
            },
          },
        },
        MuiDialog: {
          styleOverrides: {
            paper: {
              borderRadius: 16,
              backgroundImage: 'none',
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: {
              fontWeight: 500,
            },
          },
        },
        MuiTooltip: {
          styleOverrides: {
            tooltip: {
              backgroundColor: isDark ? '#293654' : BRAND.slate800,
              fontSize: '0.75rem',
              fontWeight: 500,
              borderRadius: 8,
              padding: '6px 10px',
            },
          },
        },
        MuiAppBar: {
          styleOverrides: {
            root: {
              backgroundImage: 'none',
            },
          },
        },
        MuiToggleButton: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              fontWeight: 600,
            },
          },
        },
        MuiLinearProgress: {
          styleOverrides: {
            root: {
              borderRadius: 99,
            },
          },
        },
      },
    });
  }, [mode]);

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export default ThemeContext;
